/**
 * ExoPanel U1 / Tranche F1 — render controls from derived ``responseSchema`` (A6).
 *
 * Governing: response schema is derived from the tracked artifact's unpopulated,
 * state-anchored User properties. The eight-code vocab is a *render hint* mapped
 * from that property set — not an authored activity field.
 *
 * Complete persists answers as property patches on ``responseSchemaTargetRef``.
 */
import React, { useEffect, useMemo } from 'react';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { exoSoftOperatorMessage, looksLikeKryspaceId } from './exoPanelHelpers';
import styles from '../ControlBridge.module.css';

export const USER_RESPONSE_TYPES = [
  'binary_disposition',
  'prose',
  'single_choice',
  'multi_choice',
  'property_update',
  'structured_form',
  'signoff',
  'deeplink_only',
] as const;

export type UserResponseTypeCode = (typeof USER_RESPONSE_TYPES)[number];

export interface ResponseSchemaField {
  propertyKey: string;
  globalPropertyRef?: string;
  prompt?: string;
  required?: boolean;
  enumValues?: string[];
  propertyType?: string;
  inputSource?: string | string[];
  stateActionType?: string;
  actorKind?: string;
  anchorState?: string;
}

export type ExoFieldActorKind = 'USER' | 'SYSTEM' | 'JOINT' | 'UNKNOWN';

export interface ExoUserResponseDraft {
  /** property_key → answer value */
  fieldValues: Record<string, string>;
}

export interface ExoUserResponseControlsProps {
  item: ControlBridgeQueueItem;
  draft: ExoUserResponseDraft;
  onDraftChange: (next: ExoUserResponseDraft) => void;
  onValidityChange?: (ready: boolean) => void;
}

export function emptyUserResponseDraft(): ExoUserResponseDraft {
  return { fieldValues: {} };
}

export function normalizeUserResponseType(raw: unknown): UserResponseTypeCode {
  const t = String(raw || '')
    .trim()
    .toLowerCase();
  if ((USER_RESPONSE_TYPES as readonly string[]).includes(t)) {
    return t as UserResponseTypeCode;
  }
  return 'binary_disposition';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/** Normalize snake/camel schema rows from attention-queue / eligible emit. */
export function normalizeResponseSchema(
  raw: ControlBridgeQueueItem['responseSchema']
): ResponseSchemaField[] {
  if (!Array.isArray(raw)) return [];
  const out: ResponseSchemaField[] = [];
  for (const row of raw) {
    const obj = asRecord(row);
    if (!obj) continue;
    const propertyKey = String(obj.propertyKey ?? obj.property_key ?? '').trim();
    if (!propertyKey) continue;
    const enumsRaw = obj.enumValues ?? obj.enum_values;
    const enumValues = Array.isArray(enumsRaw)
      ? enumsRaw.map((v) => String(v)).filter((v) => v.trim())
      : undefined;
    const rules = asRecord(obj.validationRules ?? obj.validation_rules);
    const rulesEnums = rules && Array.isArray(rules.enum_values)
      ? (rules.enum_values as unknown[]).map((v) => String(v)).filter((v) => v.trim())
      : undefined;
    // Prefer authored user prompts only. Never fall back to catalog ``description``
    // (Orlin B3: property definitions are unintelligible as form labels).
    const prompt =
      String(
        obj.userResponsePrompt ??
          obj.user_response_prompt ??
          obj.prompt ??
          obj.question ??
          ''
      ).trim() || undefined;
    out.push({
      propertyKey,
      globalPropertyRef: String(
        obj.globalPropertyRef ?? obj.global_property_ref ?? propertyKey
      ).trim(),
      prompt,
      required: Boolean(obj.required ?? rules?.required),
      enumValues: enumValues?.length ? enumValues : rulesEnums,
      propertyType: String(obj.propertyType ?? obj.property_type ?? '').trim() || undefined,
      inputSource: (obj.inputSource ?? obj.input_source) as string | string[] | undefined,
      stateActionType:
        String(obj.stateActionType ?? obj.state_action_type ?? '').trim() || undefined,
      actorKind: String(obj.actorKind ?? obj.actor_kind ?? '').trim() || undefined,
      anchorState: String(obj.anchorState ?? obj.anchor_state ?? '').trim() || undefined,
    });
  }
  return out;
}

function inputSourceNeedle(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw.toLowerCase();
  if (Array.isArray(raw)) return raw.map(inputSourceNeedle).join(' ');
  if (typeof raw === 'object') {
    const rec = raw as Record<string, unknown>;
    return [rec.value, rec.source, rec.input_source, rec.inputSource]
      .map(inputSourceNeedle)
      .join(' ');
  }
  return String(raw).toLowerCase();
}

/**
 * BE-PKG-2 / FE-ACT-7: classify from explicit actor_kind, then state_action_type,
 * then catalog input_source. UNKNOWN is T1 user rendering — do not guess JOINT
 * from property keys or invent conversion tables.
 */
export function exoFieldActorKind(field: ResponseSchemaField): ExoFieldActorKind {
  const explicit = String(field.actorKind ?? '').trim().toUpperCase();
  if (explicit === 'USER' || explicit === 'SYSTEM' || explicit === 'JOINT') {
    return explicit;
  }
  const sat = String(field.stateActionType ?? '').trim().toLowerCase();
  if (sat === 'user') return 'USER';
  if (sat === 'system') return 'SYSTEM';
  if (sat === 'joint' || sat === 'user_and_system' || sat === 'user+system') return 'JOINT';
  const text = inputSourceNeedle(field.inputSource);
  if (!text) return 'UNKNOWN';
  const hasUser = text.includes('user');
  const hasSystem = text.includes('system');
  if (hasUser && hasSystem) return 'JOINT';
  if (hasUser) return 'USER';
  if (hasSystem) return 'SYSTEM';
  return 'UNKNOWN';
}

/** Stored 0–10 score enums must not render as the JOINT question (SFE3). */
export function looksLikeStoredScoreEnum(values: string[] | undefined): boolean {
  if (!values || values.length < 2 || values.length > 11) return false;
  return values.every((raw) => {
    const v = raw.trim();
    if (!/^\d+$/.test(v)) return false;
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 && n <= 10;
  });
}

export function exoCollectableSchemaFields(fields: ResponseSchemaField[]): ResponseSchemaField[] {
  return fields.filter((field) => exoFieldActorKind(field) !== 'SYSTEM');
}

export function isExoSystemOnlySchema(item: ControlBridgeQueueItem): boolean {
  const fields = normalizeResponseSchema(item.responseSchema);
  if (fields.length === 0) return false;
  return exoCollectableSchemaFields(fields).length === 0;
}

/** Label for an Exo schema field — never property key or catalog definition. */
export function responseFieldPromptLabel(
  field: ResponseSchemaField,
  item?: ControlBridgeQueueItem,
  options?: { schemaFieldCount?: number }
): { label: string; hasPrompt: boolean; answerTypeHint?: string } {
  const fieldPrompt = field.prompt?.trim();
  const itemPrompt = item?.userResponsePrompt?.trim();
  const schemaFieldCount = options?.schemaFieldCount ?? 1;
  // Activity-level user_response_prompt is the ask for a one-field collect.
  // Do not stamp it onto every structured_form row (Fred ice-machine 2026-10-05).
  const label = (
    fieldPrompt ||
    (schemaFieldCount <= 1 ? itemPrompt : '') ||
    ''
  ).trim();
  const answerTypeHint = field.propertyType?.trim() || undefined;
  if (!label) {
    return {
      label: 'No question is defined for this field yet.',
      hasPrompt: false,
      answerTypeHint,
    };
  }
  return { label, hasPrompt: true, answerTypeHint };
}

export function isResponseSchemaDraftReady(
  fields: ResponseSchemaField[],
  draft: ExoUserResponseDraft
): boolean {
  const collectable = exoCollectableSchemaFields(fields);
  if (!collectable.length) return true;
  return collectable.every((f) => {
    if (!f.required) return true;
    return Boolean((draft.fieldValues[f.propertyKey] || '').trim());
  });
}

/** FE-ACT-3 / FE-ACT-13: empty prompt + empty field list is not binary Complete. */
export function exoCompletionContract(item: ControlBridgeQueueItem): {
  actionable: boolean;
  missing: string | null;
} {
  const blocked = item.responseSchemaBlockedReason?.trim();
  if (blocked) {
    return { actionable: false, missing: blocked };
  }
  const fields = normalizeResponseSchema(item.responseSchema);
  const collectable = exoCollectableSchemaFields(fields);
  if (fields.length > 0 && collectable.length === 0) {
    return {
      actionable: false,
      missing: 'Waiting on system work. This is not a person question.',
    };
  }
  const itemPrompt = item.userResponsePrompt?.trim();
  if (collectable.length === 0) {
    if (itemPrompt) {
      return { actionable: true, missing: null };
    }
    return {
      actionable: false,
      missing: 'No question is defined for this item yet.',
    };
  }
  const promptsReady = collectable.every(
    (f) => responseFieldPromptLabel(f, item, { schemaFieldCount: collectable.length }).hasPrompt
  );
  if (!promptsReady) {
    return {
      actionable: false,
      missing: 'One or more fields have no question yet.',
    };
  }
  return { actionable: true, missing: null };
}

export interface ExoChoiceOption {
  label: string;
  value: string;
}

export type ExoCardKind = 'approval' | 'instance' | 'set';

/** Operational instance vs improvement set vs binary approval (U4). */
export function exoCardKind(item: ControlBridgeQueueItem): ExoCardKind {
  if (isExoBinaryApprovalCard(item)) return 'approval';
  const hasRecord = Boolean(
    item.recordContext?.name?.trim() ||
      item.recordContext?.trackedArtifactRef?.trim() ||
      item.responseSchemaTargetRef?.trim()
  );
  const hasImprovement = Boolean(
    item.improvementTitle?.trim() || item.improvementRef?.trim()
  );
  if (hasRecord) return 'instance';
  if (hasImprovement) return 'set';
  return 'instance';
}

export function isExoBinaryApprovalCard(item: ControlBridgeQueueItem): boolean {
  const type = normalizeUserResponseType(item.userResponseType);
  if (type !== 'binary_disposition') return false;
  const fields = normalizeResponseSchema(item.responseSchema);
  if (fields.length > 0) return false;
  return Boolean(item.userResponsePrompt?.trim());
}

export function normalizeUserResponseOptions(
  raw: ControlBridgeQueueItem['userResponseOptions']
): ExoChoiceOption[] {
  if (!Array.isArray(raw)) return [];
  const out: ExoChoiceOption[] = [];
  for (const row of raw) {
    if (typeof row === 'string') {
      const value = row.trim();
      if (!value) continue;
      out.push({
        label: looksLikeKryspaceId(value) ? 'Unnamed option' : value,
        value,
      });
      continue;
    }
    if (!row || typeof row !== 'object') continue;
    const value = String(row.value ?? '').trim();
    const rawLabel = String(row.label ?? '').trim();
    const label =
      rawLabel || (value && !looksLikeKryspaceId(value) ? value : '') || (value ? 'Unnamed option' : '');
    if (!value && !label) continue;
    out.push({ label: label || value, value: value || label });
  }
  return out;
}

export function exoChoiceIsFreeText(option: ExoChoiceOption): boolean {
  const value = option.value.trim().toUpperCase();
  const label = option.label.trim().toLowerCase();
  return (
    value === 'OTHER_FREE_TEXT' ||
    label === 'someone else' ||
    label === 'other' ||
    label.startsWith('someone else')
  );
}

export function buildApprovalChangePatches(
  item: ControlBridgeQueueItem,
  value: string
): Array<{ key: string; value: string; global_property_ref: string }> {
  const v = value.trim();
  if (!v) return [];
  const keys = (item.userResponseTargetPropertyKeys ?? [])
    .map((k) => k.trim())
    .filter(Boolean);
  return keys.map((key) => ({ key, value: v, global_property_ref: key }));
}

export function buildPropertyPatchesFromDraft(
  fields: ResponseSchemaField[],
  draft: ExoUserResponseDraft
): Array<{ key: string; value: string; global_property_ref: string }> {
  const patches: Array<{ key: string; value: string; global_property_ref: string }> = [];
  for (const f of exoCollectableSchemaFields(fields)) {
    const value = (draft.fieldValues[f.propertyKey] || '').trim();
    if (!value) continue;
    patches.push({
      key: f.propertyKey,
      value,
      global_property_ref: f.globalPropertyRef || f.propertyKey,
    });
  }
  return patches;
}

export const ExoUserResponseControls: React.FC<ExoUserResponseControlsProps> = ({
  item,
  draft,
  onDraftChange,
  onValidityChange,
}) => {
  const fields = useMemo(() => normalizeResponseSchema(item.responseSchema), [item.responseSchema]);
  const collectable = useMemo(() => exoCollectableSchemaFields(fields), [fields]);
  const blockedReason = item.responseSchemaBlockedReason?.trim();
  const renderHint = normalizeUserResponseType(item.userResponseType);
  const promptsReady = collectable.every((f) =>
    responseFieldPromptLabel(f, item, { schemaFieldCount: collectable.length }).hasPrompt
  );
  const contract = exoCompletionContract(item);
  const ready =
    contract.actionable &&
    !blockedReason &&
    promptsReady &&
    isResponseSchemaDraftReady(collectable, draft);
  useEffect(() => {
    onValidityChange?.(ready);
  }, [ready, onValidityChange]);

  if (blockedReason) {
    return (
      <div
        className={styles.exoResponseBlock}
        data-testid="exo-user-response"
        data-response-type="blocked"
        role="status"
      >
        <p className={styles.exoNoteMeta} data-testid="exo-response-blocked">
          {exoSoftOperatorMessage(blockedReason)}
        </p>
      </div>
    );
  }

  if (!fields.length) {
    // Empty derived schema → binary_disposition (Complete / Do later / Blocked only).
    return null;
  }

  if (collectable.length === 0) {
    return (
      <div
        className={styles.exoResponseBlock}
        data-testid="exo-user-response"
        data-response-type="system"
        data-actor-kind="SYSTEM"
        role="status"
      >
        <p className={styles.exoNoteMeta} data-testid="exo-response-awaiting-upstream">
          Waiting on system work. This is not a person question.
        </p>
      </div>
    );
  }

  const setField = (key: string, value: string) => {
    onDraftChange({
      fieldValues: { ...draft.fieldValues, [key]: value },
    });
  };

  return (
    <div
      className={styles.exoResponseBlock}
      data-testid="exo-user-response"
      data-response-type={renderHint}
      data-schema-count={collectable.length}
    >
      <div className={styles.exoFormFields} data-testid="exo-response-fields">
        {collectable.map((field) => {
          const actorKind = exoFieldActorKind(field);
          const value = draft.fieldValues[field.propertyKey] || '';
          const { label, hasPrompt, answerTypeHint } = responseFieldPromptLabel(field, item, {
            schemaFieldCount: collectable.length,
          });
          const jointConversational = actorKind === 'JOINT';
          const hideScoreEnum = jointConversational && looksLikeStoredScoreEnum(field.enumValues);
          const typeLine =
            !jointConversational && answerTypeHint && hasPrompt ? (
              <span className={styles.exoNoteMeta} data-testid={`exo-response-type-${field.propertyKey}`}>
                Expected answer type: {answerTypeHint}
              </span>
            ) : null;
          const useEnum =
            Boolean(field.enumValues && field.enumValues.length > 0) && !hideScoreEnum;
          if (useEnum) {
            return (
              <div
                key={field.propertyKey}
                className={styles.exoFormField}
                data-actor-kind={actorKind}
              >
                <span
                  className={styles.exoFormFieldLabel}
                  data-has-prompt={hasPrompt ? 'true' : 'false'}
                >
                  {label}
                </span>
                {typeLine}
                <div
                  className={styles.exoChoiceList}
                  role="radiogroup"
                  aria-label={label}
                  data-testid={`exo-response-field-${field.propertyKey}`}
                >
                  {field.enumValues!.map((opt) => (
                    <label key={opt} className={styles.exoChoiceRow}>
                      <input
                        type="radio"
                        name={`exo-schema-${item.itemId}-${field.propertyKey}`}
                        value={opt}
                        checked={value === opt}
                        onChange={() => setField(field.propertyKey, opt)}
                        disabled={!hasPrompt}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          }
          if (jointConversational) {
            return (
              <label
                key={field.propertyKey}
                className={styles.exoFormField}
                data-actor-kind="JOINT"
              >
                <span
                  className={styles.exoFormFieldLabel}
                  data-has-prompt={hasPrompt ? 'true' : 'false'}
                >
                  {label}
                </span>
                <textarea
                  className={`${styles.exoDeferInput} ${styles.exoJointAnswer}`}
                  data-testid={`exo-response-field-${field.propertyKey}`}
                  value={value}
                  disabled={!hasPrompt}
                  rows={3}
                  onChange={(e) => setField(field.propertyKey, e.target.value)}
                />
              </label>
            );
          }
          return (
            <label
              key={field.propertyKey}
              className={styles.exoFormField}
              data-actor-kind={actorKind}
            >
              <span
                className={styles.exoFormFieldLabel}
                data-has-prompt={hasPrompt ? 'true' : 'false'}
              >
                {label}
              </span>
              {typeLine}
              <input
                type="text"
                className={styles.exoDeferInput}
                data-testid={`exo-response-field-${field.propertyKey}`}
                value={value}
                disabled={!hasPrompt}
                onChange={(e) => setField(field.propertyKey, e.target.value)}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
};
